import React, { useEffect, useMemo, useState } from "react";
import { fetchMySessions } from "../lib/api";
import { AddToCalendarButtons } from "./AddToCalendarButtons";
import { useAuth } from "../context/AuthContext";

/**
 * UpcomingSessionsCard — dashboard tile showing the user's next 7 days
 * of scheduled sessions (creator OR invitee). Renders as compact rows
 * with the local time + RSVP chip + an .ics download for accepted
 * sessions.
 *
 * Hidden entirely when the user has no upcoming sessions to keep the
 * dashboard tight — there's no "empty state" CTA here because the
 * "Schedule session" entry point lives on the team page, not the
 * dashboard. (Captains usually have at least one team open already.)
 */
export function UpcomingSessionsCard({ onToast }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    fetchMySessions(7)
      .then((d) => { if (!cancel) { setRows(Array.isArray(d) ? d : []); setLoading(false); } })
      .catch(() => { if (!cancel) { setRows([]); setLoading(false); } });
    return () => { cancel = true; };
  }, []);

  // Hide the card when empty so the dashboard stays compact.
  if (!loading && rows.length === 0) return null;

  return (
    <div className="glass rounded-3xl p-5 border border-[#14b8a6]/20" data-testid="dash-upcoming-sessions">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-calendar-bolt mr-1" />UPCOMING SESSIONS · {rows.length}
        </p>
        <span className="text-[10px] tracking-widest text-white/30">NEXT 7 DAYS</span>
      </div>
      <div className="space-y-2">
        {rows.map((s) => (
          <SessionRow key={s.session_id} sess={s} userTz={user?.tz} onToast={onToast} />
        ))}
      </div>
    </div>
  );
}

function SessionRow({ sess, userTz, onToast }) {
  const when = useMemo(() => formatLocal(sess.starts_at, userTz), [sess.starts_at, userTz]);
  const rsvp = sess.my_rsvp || "pending";
  const acceptedCount = (sess.invites || []).filter((i) => i.rsvp === "accept").length;
  const pendingCount = (sess.invites || []).filter((i) => i.rsvp === "pending").length;

  return (
    <div
      data-testid={`dash-session-row-${sess.session_id}`}
      className="flex items-start gap-3 p-2.5 rounded-2xl bg-black/30 border border-white/5"
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-[#14b8a6]/10 border border-[#14b8a6]/30 flex items-center justify-center">
        <i className="fas fa-bolt text-[#14b8a6] text-sm" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-bold text-sm truncate">{sess.title}</p>
        <p className="text-[11px] text-white/55 truncate">
          {sess.game} · <span className="text-white/75">{when}</span>
        </p>
        <p className="text-[10px] text-white/40 mt-1">
          {acceptedCount} accepted{pendingCount ? ` · ${pendingCount} pending` : ""}
        </p>
        {rsvp === "accept" && (
          <div className="mt-2">
            <AddToCalendarButtons sess={sess} compact />
          </div>
        )}
      </div>
      <RsvpChip rsvp={rsvp} />
    </div>
  );
}

function RsvpChip({ rsvp }) {
  const map = {
    accept:  { label: "GOING",   cls: "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40" },
    maybe:   { label: "MAYBE",   cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
    decline: { label: "DECLINED",cls: "bg-white/5 text-white/50 border-white/10" },
    pending: { label: "PENDING", cls: "bg-white/5 text-white/70 border-white/15" },
  };
  const { label, cls } = map[rsvp] || map.pending;
  return (
    <span className={`text-[9px] tracking-widest font-bold px-2 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}

function formatLocal(iso, tz) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt)) return iso;
  try {
    return dt.toLocaleString(undefined, {
      timeZone: tz || undefined,
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return dt.toString();
  }
}

export default UpcomingSessionsCard;
